import { Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import * as Rx from 'rxjs'
import { tap, concatMap, first } from 'rxjs/operators'
import { MagliveApiClient } from 'src/lib/api-client/maglive-api-client'
import { environment } from 'src/env/environment'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ToastrService } from 'ngx-toastr'
import {
  GiftPostRequest,
  GiftIconPostResponse,
  GiftAnimationPostResponse,
} from 'src/lib/entity/gift'

@Component({
  selector: 'maglive-admin-gift-create',
  templateUrl: './gift-create.component.html',
  styleUrls: [
    '../../../../pages/admin/startbootstrap-sb-admin-gh-pages/src/scss/styles.scss',
    './gift-create.component.scss',
  ],
})
export class AdminGiftCreateComponent implements OnInit {
  defaultGiftIconUrl: string

  // フォーム関係
  formGroup: FormGroup
  isWaiting: boolean
  selectedGiftIconFile: File
  selectedGiftAnimationFile: File

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private router: Router
  ) {}

  ngOnInit() {
    this.defaultGiftIconUrl =
      environment.baseURL.magliveWeb + 'assets/images/noimage.png'

    let numPattern = /^[0-9]+$/
    this.formGroup = this.fb.group({
      name: ['', [Validators.required]],
      point: [
        0,
        [
          Validators.required,
          Validators.min(1),
          Validators.pattern(numPattern),
          Validators.max(100000000),
        ],
      ],
      displayOrder: [
        0,
        [
          Validators.required,
          Validators.min(0),
          Validators.pattern(numPattern),
          Validators.max(100000000),
        ],
      ],
      publicStatus: [
        'PUBLIC',
        [Validators.required, Validators.pattern('PUBLIC|PRIVATE')],
      ],
      iconPath: [''],
      iconUrl: [this.defaultGiftIconUrl],
      iconFile: [null, Validators.required],
      animationPath: [''],
      animationFile: [null, Validators.required],
    })
  }

  get name() {
    return this.formGroup.get('name')
  }
  get point() {
    return this.formGroup.get('point')
  }
  get displayOrder() {
    return this.formGroup.get('displayOrder')
  }
  get publicStatus() {
    return this.formGroup.get('publicStatus')
  }
  get iconPath() {
    return this.formGroup.get('iconPath')
  }
  get iconUrl() {
    return this.formGroup.get('iconUrl')
  }
  get iconFile() {
    return this.formGroup.get('iconFile')
  }
  get animationPath() {
    return this.formGroup.get('animationPath')
  }
  get animationFile() {
    return this.formGroup.get('animationFile')
  }

  clickGiftIconButton(event: any): void {
    event.target.value = ''
  }

  changeGiftIcon(event: any): void {
    const reader = new FileReader()
    if (event.target.files.length > 0) {
      const file = event.target.files[0]

      this.selectedGiftIconFile = file
      reader.onload = (event) => {
        let image = new Image()
        image.src = reader.result as string
        this.formGroup.patchValue({
          iconUrl: reader.result,
        })
      }
      reader.readAsDataURL(file)
    }
  }

  removeSelectingGiftIcon(): void {
    if (this.iconUrl.value != this.defaultGiftIconUrl) {
      this.selectedGiftIconFile = null
      this.formGroup.patchValue({
        iconPath: null,
        iconUrl: this.defaultGiftIconUrl,
        iconFile: null,
      })
    }
  }

  clickGiftAnimationButton(event: any): void {
    event.target.value = ''
  }

  changeGiftAnimation(event: any): void {
    if (event.target.files.length > 0) {
      const file = event.target.files[0]
      this.selectedGiftAnimationFile = file
    }
  }

  removeSelectingGiftAnimation(): void {
    this.selectedGiftAnimationFile = null
    this.formGroup.patchValue({
      animationPath: null,
      animationFile: null,
    })
  }

  private createGiftIcon(
    iconFile: File
  ): Rx.Observable<'NOT_AUTHORIZED' | 'BAD_REQUEST' | GiftIconPostResponse> {
    const formData = new FormData()
    formData.append('file', iconFile)

    return MagliveApiClient.createGiftIcon(formData).pipe(
      tap((response) => {
        const result = response as GiftIconPostResponse
        this.formGroup.patchValue({
          iconPath: result.giftIconPath,
        })
      })
    )
  }

  private createGiftAnimation(
    animationFile: File
  ): Rx.Observable<
    'NOT_AUTHORIZED' | 'BAD_REQUEST' | GiftAnimationPostResponse
  > {
    const formData = new FormData()
    formData.append('file', animationFile)

    return MagliveApiClient.createGiftAnimation(formData).pipe(
      tap((response) => {
        const result = response as GiftAnimationPostResponse
        this.formGroup.patchValue({
          animationPath: result.giftAnimationPath,
        })
      })
    )
  }

  private validateCreateGift(request: GiftPostRequest): boolean {
    if (request.name == null) return false
    if (request.point == null || request.point < 1) return false
    if (request.displayOrder == null || request.displayOrder < 0) return false
    if (
      request.publicStatus == null ||
      (request.publicStatus !== 'PUBLIC' && request.publicStatus !== 'PRIVATE')
    )
      return false
    if (request.iconPath == null) return false
    if (request.animationPath == null) return false
    return true
  }

  createGift(): void {
    if (
      this.isWaiting ||
      this.selectedGiftIconFile == null ||
      this.selectedGiftAnimationFile == null
    )
      return
    this.isWaiting = true

    // アイコン登録
    const giftIconPostResponse$ = this.createGiftIcon(this.selectedGiftIconFile)
    const giftAnimationPostResponse$ = this.createGiftAnimation(
      this.selectedGiftAnimationFile
    )

    Rx.forkJoin([
      giftIconPostResponse$.pipe(first()),
      giftAnimationPostResponse$.pipe(first()),
    ])
      .pipe(
        concatMap((data) => {
          const giftIcon = data[0]
          const giftAnimation = data[1]

          if (
            giftIcon === 'NOT_AUTHORIZED' ||
            giftAnimation === 'NOT_AUTHORIZED'
          ) {
            this.toastr.error('権限エラーが発生しました')
            this.isWaiting = false
            return
          }

          if (giftIcon === 'BAD_REQUEST' || giftAnimation === 'BAD_REQUEST') {
            this.toastr.error('入力項目が正しくありません')
            this.isWaiting = false
            return
          }

          const giftPostRequest: GiftPostRequest = {
            name: this.name.value,
            point: this.point.value,
            displayOrder: this.displayOrder.value,
            publicStatus: this.publicStatus.value,
            iconPath: giftIcon.giftIconPath,
            animationPath: giftAnimation.giftAnimationPath,
          }

          if (!this.validateCreateGift(giftPostRequest)) {
            this.toastr.error('入力項目が正しくありません')
            this.isWaiting = false
            return
          }

          return MagliveApiClient.creatGift(giftPostRequest)
        })
      )
      .subscribe(
        (response) => {
          if (response === 'NOT_AUTHORIZED') {
            this.toastr.error('権限エラーが発生しました')
            this.isWaiting = false
          } else if (response === 'BAD_REQUEST') {
            this.toastr.error('入力項目が正しくありません')
            this.isWaiting = false
          } else {
            this.toastr.success('登録が完了しました')
            setTimeout(() => (this.isWaiting = false), 3000)
            this.router.navigate(['admin/gifts'])
          }
        },
        (err) => {
          console.error(err)
          this.toastr.error('登録に失敗しました')
          setTimeout(() => (this.isWaiting = false), 3000)
        }
      )
  }
}
